import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  Assignee, Ticket, TicketDetail, CaseFileEntry, ComposeDraftResult, CorrectionResult,
  CustomerCandidates, ProductCandidates, SetEntityResult, SetRequestResult,
  PurchasedFamilies, SplitProductsResponse, SetProductsResult, MergeDuplicateResponse, NotDuplicateResponse,
  ReplyDraftResult,
} from '../types/domain';

const TICKETS_KEY = ['tickets'] as const;

/**
 * The board and the review cockpit, both.
 *
 * `queryString` is the ALREADY-BUILT query (see `useTicketFilters`), not a
 * status CSV: since P22 the caller can send eight different filters and the
 * cache key has to be the exact request, or two different filter sets share one
 * cached answer. Building it here from an options object would put a second
 * serialiser next to the one the URL already uses — and the two would drift.
 *
 * `getPaged`, NOT `get` (P30): `pagination` is a SIBLING of `data` in the
 * envelope, and `get` returns `body.data` and drops everything beside it. The
 * total is what lets the board say "500 of N loaded" instead of letting a
 * column filter quietly describe a truncated page as the whole set.
 */
export function useTicketsList(queryString?: string) {
  const suffix = queryString ? `?${queryString}` : '';
  return useQuery({
    queryKey: [...TICKETS_KEY, 'list', suffix || 'default'],
    queryFn: () => api.getPaged<Ticket[]>(`/api/v1/tickets${suffix}`),
  });
}

/**
 * Who a ticket can be handed to — ACTIVE `[Dra].[UserRoles]` rows.
 *
 * Long-lived on purpose: role assignments change a few times a year, and a
 * picker that refetches on every ticket open would be noise. It is invalidated
 * by nothing here, because nothing here changes roles — Settings does.
 */
export function useAssignees() {
  return useQuery({
    queryKey: ['assignees'],
    queryFn: () => api.get<Assignee[]>('/api/v1/tickets/assignees'),
    staleTime: 5 * 60_000,
  });
}

export function useTicket(ticketNumber: string | undefined) {
  return useQuery({
    queryKey: [...TICKETS_KEY, 'detail', ticketNumber],
    queryFn: () => api.get<TicketDetail>(`/api/v1/tickets/${ticketNumber}`),
    enabled: !!ticketNumber,
  });
}

export function useTicketCaseFile(ticketNumber: string | undefined) {
  return useQuery({
    queryKey: [...TICKETS_KEY, 'case-file', ticketNumber],
    queryFn: () => api.get<CaseFileEntry[]>(`/api/v1/tickets/${ticketNumber}/case-file`),
    enabled: !!ticketNumber,
  });
}

export function useUpdateTicketStatus(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: string; closeReason?: string }) =>
      api.put<Ticket>(`/api/v1/tickets/${ticketNumber}/status`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

export function useAssignTicket(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignedTo: string | null) => api.put<Ticket>(`/api/v1/tickets/${ticketNumber}/assign`, { assignedTo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

export function useAddTicketNote(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; detail?: string }) =>
      api.post(`/api/v1/tickets/${ticketNumber}/notes`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TICKETS_KEY, 'case-file', ticketNumber] });
    },
  });
}

/**
 * Compose a reply draft for this ticket — steps 8 + 9, on demand (P14).
 *
 * `allowLanguageMismatch` is the C9 override: deliver a document in a language
 * the customer did not ask for. The endpoint takes the DECIDER'S NAME from the
 * authenticated session, never from this body — there is deliberately no
 * `overriddenBy` field to send, because that one audit row exists to name a
 * real person and a client-supplied name would forge it.
 *
 * Resolving does NOT mean a draft was created. A ticket with no active template
 * in its language legitimately comes back `outcome: 'Skipped'` and lands in
 * NeedsReview — the caller must read `outcome`, exactly like bulk-approve.
 * Invalidates the drafts cache too: a successful compose puts a new row in the
 * approval queue.
 */
export function useComposeDraft(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    // Two INDEPENDENT overrides, never one flag: the document's language and
    // the covering mail's language are different decisions with different
    // weight, and one click must not sign both.
    mutationFn: (opts?: { allowLanguageMismatch?: boolean; allowEnglishReply?: boolean }) =>
      api.post<ComposeDraftResult>(`/api/v1/tickets/${ticketNumber}/compose-draft`, {
        allowLanguageMismatch: opts?.allowLanguageMismatch === true,
        allowEnglishReply: opts?.allowEnglishReply === true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}

/**
 * =========================================================================
 * THE RECOMPUTE LANE (P21)
 * =========================================================================
 * `Reprocess` used to live here. It POSTed to `/api/v1/agent/reprocess/…`,
 * which set `Communications.ProcessingStatus='Pending'` — a field nothing reads
 * to re-run a mail. Three real clicks, zero rows, one green toast. Removed.
 *
 * What replaces it is not a re-run of the MAIL but a correction of the TICKET:
 * a person names the customer or the article, and the server re-runs steps 5,
 * 7, 8 and 9 on the ticket that already exists.
 *
 * The candidate queries are read-only and safe to keep alive while somebody
 * types; the two mutations invalidate tickets AND drafts, because a successful
 * recompute routinely puts a new row in the approval queue.
 */
/**
 * `companyCode` is part of the QUERY KEY and not only of the URL (P28).
 *
 * Switching dit -> dch -> both has to refetch: the whole point of Roman's
 * decision 7 is that the company changes which rows exist, and a cache keyed
 * only on the search term would show the previous company's list under the new
 * label.
 */
export function useCustomerCandidates(
  ticketNumber: string | undefined,
  q: string,
  enabled: boolean,
  companyCode?: string | null,
) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (companyCode) params.set('companyCode', companyCode);
  const qs = params.toString();
  return useQuery({
    queryKey: [...TICKETS_KEY, 'customer-candidates', ticketNumber, q, companyCode ?? ''],
    queryFn: () => api.get<CustomerCandidates>(
      `/api/v1/tickets/${ticketNumber}/customer-candidates${qs ? `?${qs}` : ''}`,
    ),
    enabled: !!ticketNumber && enabled,
  });
}

export function useProductCandidates(ticketNumber: string | undefined, q: string, enabled: boolean) {
  return useQuery({
    queryKey: [...TICKETS_KEY, 'product-candidates', ticketNumber, q],
    queryFn: () => api.get<ProductCandidates>(
      `/api/v1/tickets/${ticketNumber}/product-candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
    enabled: !!ticketNumber && enabled,
  });
}

/**
 * The customer's own purchase history, for a "send me everything" request
 * (P26, Roman's decision 4).
 *
 * The same list the POST validates a selection against, which is why the screen
 * never sends a name or a company — only the article ids it was offered.
 */
export function usePurchasedFamilies(ticketNumber: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...TICKETS_KEY, 'purchased-families', ticketNumber],
    queryFn: () => api.get<PurchasedFamilies>(`/api/v1/tickets/${ticketNumber}/purchased-families`),
    enabled: !!ticketNumber && enabled,
  });
}

/**
 * Raise one ticket per picked family and close the request when nothing is left.
 *
 * Nothing is drafted inside the call — N tickets would be N model calls in one
 * request. They land in `New` with a settled authorization and the agent's next
 * poll drafts them into the approval queue.
 */
export function useSplitProducts(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { productIds: string[] }) =>
      api.post<SplitProductsResponse>(`/api/v1/tickets/${ticketNumber}/split-products`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

/**
 * The two answers to a duplicate (P27, Roman's decision 3).
 *
 * Neither takes a target: the SERVER resolves which ticket this one is a
 * duplicate of, from the recorded link or from the same key the pipeline
 * dedupes on. A body-supplied target would let a merge close a ticket into an
 * unrelated case, and the audit row would faithfully record the lie.
 */
export function useMergeDuplicate(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MergeDuplicateResponse>(`/api/v1/tickets/${ticketNumber}/merge-duplicate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}

export function useNotDuplicate(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<NotDuplicateResponse>(`/api/v1/tickets/${ticketNumber}/not-duplicate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

export function useSetTicketCustomer(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { customerId: string; companyCode: string }) =>
      api.post<SetEntityResult>(`/api/v1/tickets/${ticketNumber}/customer`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      // P28: an EDIT cancels the open Sales request, so that board moved too.
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

export function useSetTicketProduct(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { productId: string }) =>
      api.post<SetEntityResult>(`/api/v1/tickets/${ticketNumber}/product`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

/**
 * SEVERAL ARTICLES AT ONCE (P29, Roman's decision 2).
 *
 * One call and not N: the first id is set on this ticket through the same path
 * `useSetTicketProduct` uses — withdrawal of a stale draft included — and the
 * rest become sibling tickets under the SAME correlation id. Sending them one
 * by one from the browser would produce N correlation ids, N chances to fail
 * half-way, and no way to tell afterwards that they were one decision.
 *
 * `tickets` is invalidated because new tickets exist; `drafts` because the
 * withdrawal may have taken one out of the queue; `authorizations` because each
 * child runs its own purchase check and may add a question for a Sales Manager.
 */
export function useSetTicketProducts(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { productIds: string[] }) =>
      api.post<SetProductsResult>(`/api/v1/tickets/${ticketNumber}/products`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

/**
 * The facts the CLASSIFIER read (P28): requested language, "yes, this is an SDS
 * request", "reply to the sender we resolved", priority and due date.
 *
 * One endpoint and not five, because they are one form. The server decides
 * which of them re-run the chain — language, document type and relay origin do;
 * priority and due date order a queue for people and are read by nothing in the
 * pipeline — and says so in `recompute`, which is null for the second kind.
 */
export function useSetTicketRequest(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      requestedLanguage?: string | null;
      docTypeConfirmed?: true;
      relayOriginConfirmed?: true;
      priority?: string;
      dueAt?: string | null;
    }) => api.post<SetRequestResult>(`/api/v1/tickets/${ticketNumber}/request`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

/**
 * "Ask again" — the P21 engine with nothing corrected (P22).
 *
 * For the tickets whose FACTS are fine and whose authorization request simply
 * expired: six live tickets sat on `AuthorizationRequestExpired` with no
 * control anywhere that could clear it, because both P21 entrances require a
 * customer or product to correct. Same engine, same one correlation id, same
 * result shape as `useSetTicketCustomer` — so the ticket screen reports the
 * outcome with the one function it already has.
 */
export function useRecomputeTicket(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SetEntityResult>(`/api/v1/tickets/${ticketNumber}/recompute`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

/**
 * Start a correction for a ticket whose reply has already been sent (P24,
 * Roman's decision 6).
 *
 * It is the ONLY door back into an answered ticket: `useUpdateTicketStatus`
 * answers 409 for a reopen with a sent draft behind it, and names this path in
 * the refusal. The new draft is a copy of the mail that went out — same thread,
 * same recipients, same candidate list — so the person edits the wording and
 * switches the document on Approvals, then approves it like any other reply.
 *
 * `reason` goes to the LEDGER, never to the customer.
 */
/**
 * Start a reply a person writes themselves (P37b) — on a ticket the agent
 * deliberately left unanswered.
 *
 * The body carries NOTHING but an optional subject: the recipient is resolved on
 * the server through the same function step 9 uses, so the relayed-mail rule
 * cannot be bypassed by a client, and the text is written afterwards in the
 * draft editor on `/approvals`. The draft starts empty and blocks its own bulk
 * approval until somebody writes in it.
 */
export function useStartReply(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: { subject?: string }) =>
      api.post<ReplyDraftResult>(`/api/v1/tickets/${ticketNumber}/reply`, body ?? {}),
    onSuccess: () => {
      // Both: the ticket's own `sendState` turns `Pending` (P37a) and the new
      // draft belongs in the approval queue this very moment.
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}

export function useCreateCorrection(ticketNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      api.post<CorrectionResult>(`/api/v1/tickets/${ticketNumber}/correction`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TICKETS_KEY });
      qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}
